/**
 * ErrorBoundary — ловит ошибки рендера и показывает понятную
 * страницу вместо белого экрана.
 *
 * Зачем: до этого в проекте ErrorBoundary не было вообще
 * (grep по componentDidCatch / getDerivedStateFromError — 0 файлов).
 * Любое исключение в render-методе любого компонента → React убирал
 * всё дерево и оставлял белый html.body. Отельеры регулярно жаловались
 * на «через 2-3 дня белый экран» — это собственно оно (см. #339, #402).
 *
 * Поведение:
 * - Ловит `render()` и `useEffect`-throw'ы (но не async-ошибки —
 *   их надо ловить try/catch в самом коде).
 * - Показывает простой fallback с кнопкой «Перезагрузить страницу»
 *   и развёрнутой ошибкой под спойлером (для саппорта).
 * - Логирует в console и (если есть) в Sentry/window.dataLayer.
 */
import React from "react";
import { Button, Result, Typography, Collapse } from "antd";

const { Paragraph, Text } = Typography;

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/** Vite режет код на chunk'и с hash в имени (DashboardPage-XXX.js).
 *  После деплоя hash меняется, а у юзера в открытой вкладке остаётся
 *  старый index.js со ссылками на старые chunks. При навигации к
 *  lazy-роуту → "Failed to fetch dynamically imported module".
 *  Ловим именно эту ошибку отдельно от обычных рендер-ошибок и
 *  делаем reload — клиент получит свежий index.js. */
const CHUNK_RELOAD_FLAG = "chunk-reloaded";

function isChunkLoadError(error: Error | null | undefined): boolean {
  const msg = String(error?.message || "");
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module")
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Chunk-load после деплоя — пробуем reload автоматически, без
    // показа красного экрана. sessionStorage-флаг страхует от
    // reload-loop'а если сервер реально отдаёт битый билд.
    if (isChunkLoadError(error)) {
      try {
        if (!sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
          window.location.reload();
        }
      } catch {
        /* sessionStorage недоступен — fall through, покажем UI */
      }
    }
    return { hasError: true, error };
  }

  componentDidMount() {
    // Если страница ожила без повторного chunk-error — сбрасываем
    // флаг, чтобы следующая deploy-инкомпатимость снова смогла reload.
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    } catch {
      /* ignore */
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
    // Если в проекте появится Sentry — добавим тут captureException.
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign("/dashboard");
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo } = this.state;

    // Chunk-load ошибка — мягкий фоллбэк без «страшных» деталей.
    // Тут оказываемся только если reload в getDerivedStateFromError
    // не сработал (sessionStorage недоступен или это уже второй
    // chunk error подряд после reload — значит билд реально битый).
    if (isChunkLoadError(error)) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "#f5f5f5",
          }}
        >
          <Result
            status="info"
            title="Обновление приложения"
            subTitle="Доступна новая версия. Перезагрузите страницу, чтобы получить актуальный билд."
            extra={[
              <Button
                type="primary"
                key="reload"
                onClick={() => {
                  try {
                    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
                  } catch {
                    /* ignore */
                  }
                  window.location.reload();
                }}
              >
                Перезагрузить страницу
              </Button>,
            ]}
          />
        </div>
      );
    }

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#f5f5f5",
        }}
      >
        <Result
          status="error"
          title="Что-то пошло не так"
          subTitle={
            "Произошла непредвиденная ошибка. Попробуйте перезагрузить "
            + "страницу. Если ошибка повторяется — отправьте детали "
            + "(под «Детали ошибки») в поддержку HKeeper."
          }
          extra={[
            <Button type="primary" key="reload" onClick={this.handleReload}>
              Перезагрузить страницу
            </Button>,
            <Button key="home" onClick={this.handleGoHome}>
              На главную
            </Button>,
          ]}
        >
          <Collapse
            ghost
            items={[
              {
                key: "details",
                label: "Детали ошибки (для поддержки)",
                children: (
                  <div style={{ maxHeight: 320, overflow: "auto" }}>
                    <Paragraph>
                      <Text strong>Сообщение: </Text>
                      <Text code>{error?.message || "—"}</Text>
                    </Paragraph>
                    {error?.stack && (
                      <Paragraph>
                        <Text strong>Stack trace:</Text>
                        <pre
                          style={{
                            fontSize: 11,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {error.stack}
                        </pre>
                      </Paragraph>
                    )}
                    {errorInfo?.componentStack && (
                      <Paragraph>
                        <Text strong>Component stack:</Text>
                        <pre
                          style={{
                            fontSize: 11,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {errorInfo.componentStack}
                        </pre>
                      </Paragraph>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </Result>
      </div>
    );
  }
}

export default ErrorBoundary;
